// Kapcsolattartó — jobb oldali beúszó FormSheet, KÉT módban (a munkatárs-minta: külön create/edit instance).
// CREATE (`contact == null`): partner-provisioning — e-mail (kulcs) + nevek + partner-profil (becenév/telefon/
// beosztás). Az Odin az identity-t `type:"partner"`-ként hozza létre (a body-ban NINCS `type`). EDIT: az
// identity-mag + partner-profil PATCH-e.
//
// FIÓK-VÁLASZTÓ ÉS ELSŐDLEGES-KAPCSOLÓ ITT NINCS (user-döntés): egy kapcsolattartó akár húsz fiókhoz is tartozhat,
// és a fiókhoz rendelést — portál-hozzáféréssel, szereppel, elsődleges jelöléssel — a FIÓK oldala adja (a
// „Kapcsolattartók" szekció kötés-szerkesztője). Az egy-fiókos választó itt csak az elsőt mutatta, a többit nem.
// Az e-mail-ütközés → az e-mail mezőre vezetett hiba. A validálás forrás-igazsága a @atlas/contracts.
import { type ContactRowSchema } from '@atlas/contracts';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { TextField } from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Text } from '@/components/ui/text';
import { contactKeys } from '@/features/contacts/api';
import {
  buildContactCreateResolver,
  buildContactResolver,
  EMPTY_CONTACT_FORM,
  toContactBody,
  toContactCreateBody,
  type ContactFormValues,
} from '@/features/contacts/form';
import { api } from '@/lib/api';
import { formatName } from '@/lib/format-name';
import { useEntityMutation } from '@/lib/use-entity-mutation';

type ContactRow = z.infer<typeof ContactRowSchema>;

export interface ContactFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` → LÉTREHOZÁS (create); egyébként a szerkesztendő kapcsolattartó (előtöltés). */
  contact: ContactRow | null;
}

export function ContactFormSheet({
  open,
  onOpenChange,
  contact,
}: ContactFormSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const isCreate = contact === null;

  const form = useForm<ContactFormValues>({
    resolver: isCreate ? buildContactCreateResolver(t) : buildContactResolver(t),
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: EMPTY_CONTACT_FORM,
  });
  const { reset, handleSubmit, setError } = form;

  // Előtöltés: edit → a sorból; create → üres.
  useEffect(() => {
    if (contact) {
      reset({
        ...EMPTY_CONTACT_FORM,
        email: contact.email ?? '',
        givenName: contact.givenName ?? '',
        familyName: contact.familyName ?? '',
        phone: contact.phone ?? '',
        // A beosztás (jobTitle) a sor `position` mezőjéből; a becenév a sor `nickname` mezőjéből (előtöltés —
        // az Odin listIdentities projektálja). A mentés dirty-alapon is véd: az érintetlen mező nem megy.
        jobTitle: contact.position ?? '',
        nickname: contact.nickname ?? '',
      });
    } else {
      reset(EMPTY_CONTACT_FORM);
    }
  }, [contact, reset]);

  const mutation = useEntityMutation<ContactFormValues>({
    mutationFn: (values) =>
      isCreate
        ? api.contactCreate.mutation({ body: toContactCreateBody(values) })
        : api.contactUpdate.mutation({
            params: { id: contact.id },
            // A becenevet CSAK akkor küldjük, ha a mezőt piszkálták (dirty) — így egy másik mező mentése nem
            // nullázza némán a meglévő becenevet (az Odin read még nem projektálja → előtöltés üres lehet).
            body: toContactBody(values, {
              includeNickname: form.formState.dirtyFields.nickname === true,
            }),
          }),
    invalidateKeys: [contactKeys.lists()],
    onFieldErrors: (field, messages) => {
      // Az e-mail-ütközést LOKALIZÁLT üzenettel vezetjük az e-mail mezőre (a nyers szerver-szöveg helyett); a
      // többi mező-hiba a szerver üzenetét kapja.
      if (field === 'email') {
        setError('email', { message: t('validation:contacts.emailConflict') });
        return;
      }
      if (field in EMPTY_CONTACT_FORM) {
        setError(field as keyof ContactFormValues, { message: messages.join(' ') });
      }
    },
    messages: {
      success: isCreate ? t('toast:contacts.created') : t('toast:contacts.updated'),
      conflict: t('toast:contacts.conflict'),
      forbidden: t('toast:contacts.forbidden'),
      validation: t('toast:contacts.validation'),
      error: isCreate ? t('toast:contacts.createError') : t('toast:contacts.error'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, { onSuccess: () => onOpenChange(false) });
  });

  // Az EDIT-fejléc alcíme: a szerkesztendő kontakt neve (a lista formatName-konvenciója szerint).
  const identityName = contact
    ? formatName(
        {
          givenName: contact.givenName,
          familyName: contact.familyName,
          displayName: contact.displayName ?? contact.email,
        },
        i18n.language,
      )
    : '';

  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset(EMPTY_CONTACT_FORM);
        onOpenChange(o);
      }}
      title={isCreate ? t('contacts.createTitle') : t('contacts.editTitle')}
      description={isCreate ? t('contacts.createDesc') : identityName || undefined}
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('contacts.cancel')}
          </Button>
          <Button type="submit" variant="brand" disabled={mutation.isPending}>
            {mutation.isPending ? t('contacts.saving') : t('contacts.save')}
          </Button>
        </>
      }
    >
      <Form {...form}>
        {/* Identity-mag — az e-mail a kulcs (edit → identity-core PATCH, e-mail-ütközés → az e-mail mezőre vezetve). */}
        <TextField<ContactFormValues>
          name="email"
          label={t('contacts.fields.email')}
          hint={t('contacts.fields.emailHint')}
          required
          type="email"
          autoComplete="off"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField<ContactFormValues>
            name="familyName"
            label={t('contacts.fields.familyName')}
            required
            autoComplete="off"
          />
          <TextField<ContactFormValues>
            name="givenName"
            label={t('contacts.fields.givenName')}
            required
            autoComplete="off"
          />
        </div>
        {/* Becenév + telefon EGY SORBAN, ebben a sorrendben (user-döntés) — ugyanaz a rend, mint a
            munkatárs-űrlapon, és ugyanaz a becenév-súgó, mint ott és a Profilomban. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField<ContactFormValues>
            name="nickname"
            label={t('contacts.fields.nickname')}
            hint={t('contacts.fields.nicknameHint')}
            autoComplete="off"
          />
          <TextField<ContactFormValues>
            name="phone"
            label={t('contacts.fields.phone')}
            type="tel"
            autoComplete="off"
          />
        </div>
        <TextField<ContactFormValues>
          name="jobTitle"
          label={t('contacts.fields.jobTitle')}
          autoComplete="off"
        />

        {/* A fiókhoz rendelés (portál-hozzáférés, szerep, elsődleges jelölés) a FIÓK oldalán történik — a
            sáv ezt kimondja, hogy senki ne itt keresse. */}
        <Text variant="secondary" as="p" className="text-[12px]">
          {t('contacts.fields.accountAssignmentHint')}
        </Text>
      </Form>
    </FormSheet>
  );
}
